import { Component, inject, ViewEncapsulation } from '@angular/core';
import { Dialog } from 'primeng/dialog';
import { SessionTimeoutProvider } from './session-timeout-provider';

/**
 * Diálogo accesible (PrimeNG) que muestra el aviso de sesión por expirar.
 * Se monta en el AppComponent y reacciona a las signals del notifier:
 * `@if (provider.state().warningVisible)` muestra el p-dialog; la cuenta
 * regresiva viene de `provider.state().countdownSeconds`.
 */
@Component({
  selector: 'app-session-timeout-dialog',
  standalone: true,
  imports: [Dialog],
  encapsulation: ViewEncapsulation.None,
  template: `
    @if (provider.state().warningVisible) {
      <p-dialog
        [visible]="true"
        [modal]="true"
        [closable]="false"
        [dismissableMask]="false"
        [closeOnEscape]="false"
        [draggable]="false"
        [resizable]="false"
        [showHeader]="false"
        [styleClass]="'sf-timeout-dialog'"
        [style]="{ width: '400px' }"
      >
        <div class="sf-timeout">
          <div class="sf-timeout__icon"><span>⏱️</span></div>
          <h2 class="sf-timeout__title">Su sesión está por expirar</h2>
          <p class="sf-timeout__text">{{ provider.state().message }}</p>
          <div class="sf-timeout__countdown" role="timer" aria-live="polite">
            {{ formatCountdown(provider.state().countdownSeconds) }}
          </div>
          <div class="sf-timeout__actions">
            @if (provider.state().reason === 'inactivity') {
              <button type="button" class="sf-timeout__primary" (click)="provider.notifier.continueSession()">
                Continuar sesión
              </button>
            }
            <button
              type="button"
              [class]="provider.state().reason === 'inactivity' ? 'sf-timeout__secondary' : 'sf-timeout__primary'"
              (click)="provider.notifier.closeSession()"
            >
              {{ provider.state().reason === 'inactivity' ? 'Cerrar sesión' : 'Entendido' }}
            </button>
          </div>
        </div>
      </p-dialog>
    }
  `,
  styles: [
    `
      .sf-timeout-dialog .p-dialog {
        background: var(--bg-surface-raised);
        border: 1px solid var(--border-default);
        border-radius: var(--border-radius-2xl);
        box-shadow: var(--shadow-xl);
        overflow: hidden;
      }
      .sf-timeout-dialog .p-dialog-content {
        background: var(--bg-surface-raised);
        color: var(--text-primary);
        padding: 0;
      }
      .sf-timeout-dialog .sf-timeout {
        text-align: center;
        font-family: var(--font-family);
        padding: var(--space-8) var(--space-6);
      }
      .sf-timeout-dialog .sf-timeout__icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 3.5rem;
        height: 3.5rem;
        margin-bottom: var(--space-4);
        border-radius: var(--border-radius-full);
        background: var(--color-primary-subtle);
        border: 1px solid var(--border-default);
        font-size: 1.75rem;
        line-height: 1;
      }
      .sf-timeout-dialog .sf-timeout__title {
        color: var(--text-primary);
        margin: 0 0 var(--space-2);
        font-size: var(--font-size-xl);
        font-weight: var(--font-weight-bold);
      }
      .sf-timeout-dialog .sf-timeout__text {
        color: var(--text-secondary);
        font-size: var(--font-size-md);
        margin: 0 0 var(--space-4);
        line-height: 1.5;
      }
      .sf-timeout-dialog .sf-timeout__countdown {
        font-size: var(--font-size-4xl);
        font-weight: var(--font-weight-extrabold);
        color: var(--color-warning);
        margin-bottom: var(--space-6);
        font-variant-numeric: tabular-nums;
        letter-spacing: 0.02em;
      }
      .sf-timeout-dialog .sf-timeout__actions {
        display: flex;
        gap: var(--space-3);
        justify-content: center;
      }
      .sf-timeout-dialog .sf-timeout__primary {
        flex: 1.2;
        padding: var(--space-3) var(--space-4);
        border: 1px solid transparent;
        border-radius: var(--border-radius-md);
        background: var(--color-primary);
        color: var(--text-on-accent);
        font-family: var(--font-family);
        font-size: var(--font-size-md);
        font-weight: var(--font-weight-bold);
        cursor: pointer;
        transition: background var(--transition-fast), transform var(--transition-fast);
      }
      .sf-timeout-dialog .sf-timeout__primary:hover {
        background: var(--color-primary-hover);
      }
      .sf-timeout-dialog .sf-timeout__primary:active {
        transform: scale(0.97);
      }
      .sf-timeout-dialog .sf-timeout__secondary {
        flex: 1;
        padding: var(--space-3) var(--space-4);
        border: 1px solid var(--border-default);
        border-radius: var(--border-radius-md);
        background: transparent;
        color: var(--text-secondary);
        font-family: var(--font-family);
        font-size: var(--font-size-md);
        font-weight: var(--font-weight-semibold);
        cursor: pointer;
        transition: background var(--transition-fast), border-color var(--transition-fast), color var(--transition-fast);
      }
      .sf-timeout-dialog .sf-timeout__secondary:hover {
        background: var(--bg-surface-hover);
        border-color: var(--border-hover);
        color: var(--text-primary);
      }
      .sf-timeout-dialog .sf-timeout__primary:focus-visible,
      .sf-timeout-dialog .sf-timeout__secondary:focus-visible {
        outline: 2px solid var(--color-primary);
        outline-offset: 2px;
      }
    `,
  ],
})
export class SessionTimeoutDialogComponent {
  protected readonly provider = inject(SessionTimeoutProvider);

  protected formatCountdown(seconds: number): string {
    const safe = Math.max(0, seconds);
    const min = Math.floor(safe / 60);
    const sec = safe % 60;
    return `${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  }
}
